// Detects the replay-log format from raw file bytes (or pasted text), and
// transparently gunzips gzipped input (real .mjlog files are gzip-compressed).

export type LogFormat = "mjai" | "mjlog" | "tenhou";

export class UnrecognizedFormatError extends Error {
    constructor() {
        super(
            "unrecognized log format: expected mjai JSONL, a Tenhou mjlog (XML), " +
                "tenhou JSON, or a tenhou.net/6/#json=... URL",
        );
        this.name = "UnrecognizedFormatError";
    }
}

async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
    const stream = new Blob([bytes as BlobPart])
        .stream()
        .pipeThrough(new DecompressionStream("gzip"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function detectFormat(
    input: Uint8Array | string,
): Promise<{ format: LogFormat; text: string }> {
    let text: string;
    if (typeof input === "string") {
        text = input.trim();
    } else {
        if (input[0] === 0x1f && input[1] === 0x8b) {
            return detectFormat(await gunzip(input));
        }
        text = new TextDecoder().decode(input).trim();
    }

    if (text.includes("<mjloggm")) return { format: "mjlog", text };
    if (text.includes("#json=") || /^%7B/i.test(text)) return { format: "tenhou", text };
    if (text.startsWith("{")) {
        try {
            const whole = JSON.parse(text) as Record<string, unknown>;
            if (Array.isArray(whole.log) && Array.isArray(whole.name)) {
                return { format: "tenhou", text };
            }
            if (typeof whole.type === "string") return { format: "mjai", text };
        } catch {
            // Not a single JSON document; JSONL has one object per line.
            try {
                const first = JSON.parse(text.split("\n", 1)[0] as string) as Record<
                    string,
                    unknown
                >;
                if (typeof first.type === "string") return { format: "mjai", text };
            } catch {
                // fall through
            }
        }
    }
    throw new UnrecognizedFormatError();
}
